import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api, { bootstrapAuth, refreshAccessToken, setAccessToken, clearAccessToken } from "./api";
import { isRecordingActive } from "./authPolicy";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  // Mirror of "is the user currently authenticated", read by the stable
  // `refresh` callback so it does NOT need `user` in its dependency array.
  // (Depending on `user` made `refresh` rebuild on every /me success, which
  // re-fired the bootstrap effect → infinite /auth/refresh + /auth/me loop.)
  const authedRef = useRef(false);

  const clearState = useCallback(() => {
    authedRef.current = false;
    setUser(null);
    setWorkspace(null);
    setMember(null);
  }, []);

  const refresh = useCallback(async () => {
    const applyMe = (data) => {
      authedRef.current = true;
      setUser(data.user);
      setWorkspace(data.workspace);
      setMember(data.member);
    };
    try {
      const { data } = await api.get("/auth/me");
      applyMe(data);
      return data;
    } catch (err) {
      // A 401 on /me almost always means the SHORT access token expired while
      // the refresh cookie is still valid (idle tab, tab-refocus re-check).
      // Mint a new access token and retry ONCE before concluding the session
      // is dead — this is what keeps a returning/idle user logged in.
      if (err?.response?.status === 401) {
        const ok = await refreshAccessToken();
        if (ok) {
          try {
            const { data } = await api.get("/auth/me");
            applyMe(data);
            return data;
          } catch {
            /* fall through to confirmed-dead handling */
          }
        }
      }
      const wasAuthenticated = authedRef.current;
      clearState();
      if (wasAuthenticated && err?.response?.status === 401) {
        const path = window.location.pathname;
        const publicRoutes = ["/login", "/signup", "/auth/callback", "/forbidden"];
        const isPublic =
          publicRoutes.some((p) => path === p || path.startsWith(p + "/")) ||
          path.startsWith("/invite/");
        if (!isPublic) {
          clearAccessToken();
          window.location.assign("/login?reason=session_expired");
        }
      }
      return null;
    }
  }, [clearState]);

  useEffect(() => {
    // If returning from Emergent OAuth callback, skip — AuthCallback bootstraps.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    (async () => {
      // Mint an access token from the httpOnly refresh cookie (if a session
      // exists), then load /me. Guests (no cookie) skip the /me call.
      const ok = await bootstrapAuth();
      if (ok) {
        await refresh();
      } else {
        clearState();
      }
      setLoading(false);
    })();
  }, [refresh, clearState]);

  // Subscribe to global "session invalidated" events from the axios interceptor.
  // When a 401 fires anywhere in the app, we wipe local React state immediately
  // (api.js handles the hard navigation to /login separately).
  useEffect(() => {
    const onInvalidated = () => clearState();
    window.addEventListener("hireproof:auth-invalidated", onInvalidated);
    return () => window.removeEventListener("hireproof:auth-invalidated", onInvalidated);
  }, [clearState]);

  // When the tab regains focus, re-verify auth so a user who comes back after
  // their cookie/JWT expired sees /login on the next paint — not stale data.
  // EXCEPTION: never re-validate (and risk a logout) while a recording is
  // active — an alt-tab to the meeting must not abandon the interview.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible" && authedRef.current && !isRecordingActive()) {
        refresh();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [refresh]);

  const loginWithToken = async (token) => {
    // Login/signup return the access token in the body (+ set the refresh
    // cookie). Hold the access token in memory, then load the profile.
    setAccessToken(token);
    await refresh();
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Ignore errors; still clear local state.
    }
    clearAccessToken();
    clearState();
  };

  return (
    <AuthContext.Provider
      value={{ user, workspace, member, loading, refresh, loginWithToken, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside <AuthProvider>");
  return ctx;
}
